/**
 * Rules shared by every standard: the drawing's consistency (codes, plan ↔ diagram, sheets,
 * control circuits), the title block and the calculations of IEC 60364-5-52, on which the RGIE,
 * NF C 15-100 and NEN 1010 all rest. Packs may reword them with `variant`.
 */
import { surveySignature, type Element, type Id } from '@elec/domain';
import { controlInfo, pointKind, staleMultiLines } from '@elec/analysis';
import { elementBounds, getSymbol } from '@elec/symbols';
import { paperSize } from '@elec/layout';
import { projectCalcs } from '@elec/calc';
import { byId, fmt, ids, name, rule, type Check, type L } from './core.ts';

export const COMMON = byId([
  rule(
    {
      id: 'R-DOC-02',
      severity: 'error',
      title: { fr: 'Cartouche incomplet', en: 'Title block incomplete', nl: 'Titelvak onvolledig' },
      why: {
        fr: "Le schéma doit identifier le propriétaire, l'adresse, la date, l'auteur, le schéma de liaison à la terre et la tension d'alimentation.",
        en: 'The diagram must identify the owner, address, date, author, earthing system and supply voltage.',
        nl: 'Het schema moet de eigenaar, het adres, de datum, de tekenaar, het aardingsstelsel en de voedingsspanning vermelden.',
      },
      fix: {
        fr: 'Complétez Fichier → Informations du projet.',
        en: 'Fill in File → Project information.',
        nl: 'Vul Bestand → Projectgegevens in.',
      },
      verified: true,
    },
    ({ project }) => {
      const m = project.meta;
      const missing: L[] = [];
      if (!m.ownerName) missing.push({ fr: 'propriétaire', en: 'owner', nl: 'eigenaar' });
      if (!m.ownerAddress && !m.installationAddress)
        missing.push({ fr: 'adresse', en: 'address', nl: 'adres' });
      if (!m.author && !m.company) missing.push({ fr: 'auteur', en: 'author', nl: 'tekenaar' });
      if (!m.date) missing.push({ fr: 'date', en: 'date', nl: 'datum' });
      if (!m.supplyVoltage) missing.push({ fr: 'tension', en: 'voltage', nl: 'spanning' });
      return missing.length
        ? [
            {
              targets: [],
              message: {
                fr: `Cartouche : ${missing.map((x) => x.fr).join(', ')} manquant(s)`,
                en: `Title block: missing ${missing.map((x) => x.en).join(', ')}`,
                nl: `Titelvak: ontbreekt ${missing.map((x) => x.nl).join(', ')}`,
              },
            },
          ]
        : [];
    },
  ),
  rule(
    {
      id: 'R-CIR-04',
      severity: 'error',
      title: {
        fr: 'Section du câble inconnue',
        en: 'Cable section missing',
        nl: 'Doorsnede van de kabel ontbreekt',
      },
      why: {
        fr: 'Chaque circuit doit indiquer le type et la section des conducteurs.',
        en: 'Every circuit must state the conductor type and section.',
        nl: 'Elke kring moet het type geleider en de doorsnede vermelden.',
      },
      fix: {
        fr: 'Complétez la colonne Câble dans le tableau des circuits.',
        en: 'Fill in the Cable column in the circuit table.',
        nl: 'Vul de kolom Kabel in de kringentabel in.',
      },
      verified: true,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => !(c.cable.sectionMm2 > 0))
        .map((c) => ({
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `Circuit ${name(c)} : section du câble à compléter`,
            en: `Circuit ${name(c)}: cable section to fill in`,
            nl: `Kring ${name(c)}: doorsnede van de kabel in te vullen`,
          },
        })),
  ),
  rule(
    {
      id: 'R-DEV-01',
      severity: 'error',
      title: {
        fr: 'Caractéristique de protection inconnue',
        en: 'Protection value unknown',
        nl: 'Kenmerk van de beveiliging onbekend',
      },
      why: {
        fr: 'Le calibre et la courbe d’un disjoncteur ou d’un fusible, la sensibilité et le type d’un différentiel doivent être connus pour vérifier l’installation (souvent illisibles sur une photo importée).',
        en: 'A breaker’s or fuse’s rating and curve, and an RCD’s sensitivity and type, must be known to check the installation (often unreadable on an imported photo).',
        nl: 'De nominale stroom en curve van een automaat of zekering, en de gevoeligheid en het type van een differentieel moeten gekend zijn om de installatie te controleren (vaak onleesbaar op een geïmporteerde foto).',
      },
      fix: {
        fr: 'Relevez la valeur sur l’appareil et complétez-la dans ses propriétés.',
        en: 'Read the value on the device and fill it in its properties.',
        nl: 'Lees de waarde af op het toestel en vul ze in bij de eigenschappen.',
      },
      verified: true,
    },
    ({ project }) =>
      Object.values(project.elements).flatMap((e) => {
        const protection = ['iec.breaker', 'iec.fuse', 'iec.rcbo', 'iec.rcd'].some(
          (s) => e.symbolId === s || e.symbolId.startsWith(`${s}-`),
        );
        if (!protection) return [];
        const rcd = e.symbolId.startsWith('iec.rcd') || e.symbolId === 'iec.rcbo';
        const missing: L[] = [];
        if (e.props.ratingA === '') missing.push({ fr: 'calibre', en: 'rating', nl: 'nominale stroom' });
        if (e.props.curve === '') missing.push({ fr: 'courbe', en: 'curve', nl: 'curve' });
        if (rcd && e.props.sensitivityMa === '')
          missing.push({ fr: 'sensibilité', en: 'sensitivity', nl: 'gevoeligheid' });
        if (rcd && e.props.rcdType === '') missing.push({ fr: 'type', en: 'type', nl: 'type' });
        if (!missing.length) return [];
        const list = (k: keyof L) => missing.map((m) => m[k]).join(', ');
        return [
          {
            targets: [e.id],
            sheetId: e.sheetId,
            message: {
              fr: `${e.tag} : ${list('fr')} à relever`,
              en: `${e.tag}: ${list('en')} to read on the device`,
              nl: `${e.tag}: ${list('nl')} af te lezen op het toestel`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-CIR-06',
      severity: 'warning',
      title: { fr: 'Circuit sans point', en: 'Circuit with no points', nl: 'Kring zonder punten' },
      why: {
        fr: 'Un circuit doit indiquer ce qu’il alimente.',
        en: 'A circuit must show what it feeds.',
        nl: 'Een kring moet tonen wat hij voedt.',
      },
      fix: {
        fr: 'Ajoutez les points du circuit (prises, points lumineux, appareils).',
        en: 'Add the circuit’s points (sockets, lights, appliances).',
        nl: 'Voeg de punten van de kring toe (stopcontacten, lichtpunten, toestellen).',
      },
      verified: true,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => c.points.length === 0 && !c.circuit?.spare)
        .map((c) => ({
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `Circuit ${name(c)} : aucun point raccordé`,
            en: `Circuit ${name(c)}: no points connected`,
            nl: `Kring ${name(c)}: geen punten aangesloten`,
          },
        })),
  ),
  rule(
    {
      id: 'R-CIR-07',
      severity: 'warning',
      title: {
        fr: 'Protection sans lettre de circuit',
        en: 'Protective device without circuit letter',
        nl: 'Beveiliging zonder kringletter',
      },
      why: {
        fr: 'Chaque circuit est identifié par une lettre reprise sur le plan de position.',
        en: 'Each circuit is identified by a letter repeated on the position plan.',
        nl: 'Elke kring wordt aangeduid met een letter die terugkomt op het situatieschema.',
      },
      fix: {
        fr: 'Utilisez « Numéroter les circuits ».',
        en: 'Use “Number circuits”.',
        nl: 'Gebruik “Kringen nummeren”.',
      },
      verified: true,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => !c.code && c.points.length > 0)
        .map((c) => ({
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `${c.device.tag} : pas de lettre de circuit`,
            en: `${c.device.tag}: no circuit letter`,
            nl: `${c.device.tag}: geen kringletter`,
          },
        })),
  ),
  rule(
    {
      id: 'R-DOC-01',
      severity: 'error',
      title: { fr: 'Code de point incorrect', en: 'Wrong point code', nl: 'Verkeerde puntcode' },
      why: {
        fr: 'Chaque point porte la lettre de son circuit suivie d’un numéro (A1, A2…), unique.',
        en: 'Each point carries its circuit letter and a number (A1, A2…), unique.',
        nl: 'Elk punt draagt de letter van zijn kring en een nummer (A1, A2…), uniek.',
      },
      fix: {
        fr: 'Utilisez « Numéroter les circuits ».',
        en: 'Use “Number circuits”.',
        nl: 'Gebruik “Kringen nummeren”.',
      },
      verified: true,
    },
    ({ a }) => {
      const out: ReturnType<Check> = [];
      const seen = new Map<string, Element>();
      for (const c of a.circuits) {
        if (!c.code) continue;
        const re = new RegExp(`^${c.code}\\d+$`);
        for (const p of c.points) {
          const tag = p.element.tag;
          if (!re.test(tag))
            out.push({
              targets: [p.element.id],
              sheetId: c.sheet.id,
              message: {
                fr: `${tag || 'Point sans code'} : devrait commencer par ${c.code}`,
                en: `${tag || 'Uncoded point'}: should start with ${c.code}`,
                nl: `${tag || 'Punt zonder code'}: moet beginnen met ${c.code}`,
              },
            });
          else if (seen.has(tag))
            out.push({
              targets: [p.element.id, seen.get(tag)!.id],
              sheetId: c.sheet.id,
              message: {
                fr: `${tag} : code en double`,
                en: `${tag}: duplicate code`,
                nl: `${tag}: dubbele code`,
              },
            });
          seen.set(tag, p.element);
        }
      }
      return out;
    },
  ),
  rule(
    {
      id: 'R-DOC-03',
      severity: 'error',
      title: {
        fr: 'Plan de position incomplet',
        en: 'Position plan incomplete',
        nl: 'Situatieschema onvolledig',
      },
      why: {
        fr: 'Chaque point du schéma unifilaire doit figurer sur le plan de position avec le même code, et inversement.',
        en: 'Every point of the single-line diagram must appear on the position plan with the same code, and vice versa.',
        nl: 'Elk punt van het eendraadschema moet met dezelfde code op het situatieschema staan, en omgekeerd.',
      },
      fix: {
        fr: 'Sans plan, ajoutez une feuille « Plan de position » (le + à côté des onglets) ; placez ensuite les points manquants depuis la liste « À placer sur le plan » de la palette.',
        en: 'With no plan, add a “Position plan” sheet (the + next to the sheet tabs); then place the missing points from the palette’s “To place on the plan” list.',
        nl: 'Zonder plan: voeg een blad “Situatieschema” toe (de + naast de tabbladen); plaats daarna de ontbrekende punten vanuit de lijst “Te plaatsen op het plan” in het palet.',
      },
      verified: true,
    },
    ({ project, a, planSheets, standard }) => {
      // A position plan is mandatory in Belgium only; elsewhere it is checked when there is one.
      if (!planSheets.length)
        return standard !== 'rgie'
          ? []
          : [
              {
                severity: 'warning',
                targets: [],
                message: {
                  fr: 'Aucun plan de position dans le projet',
                  en: 'No position plan in the project',
                  nl: 'Geen situatieschema in het project',
                },
              },
            ];
      const onPlan = new Map<string, Element>();
      for (const e of Object.values(project.elements))
        if (planSheets.includes(e.sheetId) && pointKind(e) && e.tag) onPlan.set(e.tag, e);
      const inDiagram = new Map<string, Element>();
      // Existing parts of old installations ("partie ancienne"): the correspondence between
      // diagram and plan is not required (AREI 8.2.1).
      const legacy = new Set<string>();
      for (const c of a.circuits)
        for (const p of c.points)
          if (p.element.tag) {
            inDiagram.set(p.element.tag, p.element);
            if (c.circuit?.legacy) legacy.add(p.element.tag);
          }
      const out: ReturnType<Check> = [];
      const missingOnPlan = [...inDiagram].filter(([t]) => !onPlan.has(t) && !legacy.has(t));
      if (missingOnPlan.length)
        out.push({
          targets: missingOnPlan.map(([, e]) => e.id),
          message: {
            fr: `Absents du plan : ${missingOnPlan.map(([t]) => t).join(', ')}`,
            en: `Missing from the plan: ${missingOnPlan.map(([t]) => t).join(', ')}`,
            nl: `Ontbreekt op het plan: ${missingOnPlan.map(([t]) => t).join(', ')}`,
          },
        });
      const extra = [...onPlan].filter(([t]) => !inDiagram.has(t));
      if (extra.length)
        out.push({
          targets: extra.map(([, e]) => e.id),
          sheetId: extra[0]![1].sheetId,
          message: {
            fr: `Sur le plan mais pas au schéma : ${extra.map(([t]) => t).join(', ')}`,
            en: `On the plan but not in the diagram: ${extra.map(([t]) => t).join(', ')}`,
            nl: `Op het plan maar niet in het schema: ${extra.map(([t]) => t).join(', ')}`,
          },
        });
      return out;
    },
  ),
  rule(
    {
      id: 'R-TOPO-01',
      severity: 'warning',
      title: { fr: 'Élément non raccordé', en: 'Unconnected element', nl: 'Niet-aangesloten element' },
      why: {
        fr: 'Un élément sans fil n’est rattaché à aucun circuit.',
        en: 'An element without a wire belongs to no circuit.',
        nl: 'Een element zonder draad hoort bij geen enkele kring.',
      },
      fix: {
        fr: 'Raccordez-le avec l’outil Fil (W).',
        en: 'Connect it with the Wire tool (W).',
        nl: 'Sluit het aan met het gereedschap Draad (W).',
      },
      verified: true,
    },
    ({ a }) =>
      a.unconnected
        .filter((e) => !getSymbol(e.symbolId)?.category.startsWith('earth'))
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: {
            fr: `${e.tag || getSymbol(e.symbolId)?.name.fr} : non raccordé`,
            en: `${e.tag || getSymbol(e.symbolId)?.name.en}: not connected`,
            nl: `${e.tag || getSymbol(e.symbolId)?.name.nl}: niet aangesloten`,
          },
        })),
  ),
  rule(
    {
      id: 'R-TOPO-02',
      severity: 'error',
      title: { fr: 'Point sans protection', en: 'Point without protection', nl: 'Punt zonder beveiliging' },
      why: {
        fr: 'Chaque point doit être alimenté par un circuit protégé par un disjoncteur ou un fusible.',
        en: 'Every point must be fed by a circuit protected by a breaker or fuse.',
        nl: 'Elk punt moet gevoed worden door een kring beveiligd door een automaat of smeltveiligheid.',
      },
      fix: {
        fr: 'Raccordez le point en aval d’une protection.',
        en: 'Connect the point downstream of a protective device.',
        nl: 'Sluit het punt aan stroomafwaarts van een beveiliging.',
      },
      verified: true,
    },
    ({ a }) =>
      a.unprotected.map((e) => ({
        targets: [e.id],
        sheetId: e.sheetId,
        message: {
          fr: `${e.tag || 'Point'} : aucune protection en amont`,
          en: `${e.tag || 'Point'}: no protective device upstream`,
          nl: `${e.tag || 'Punt'}: geen beveiliging stroomopwaarts`,
        },
      })),
  ),
  rule(
    {
      id: 'R-PLAN-01',
      severity: 'info',
      title: { fr: 'Point hors pièce', en: 'Point outside any room', nl: 'Punt buiten elke ruimte' },
      why: {
        fr: 'La pièce d’un point sert à vérifier les exigences des pièces humides.',
        en: 'A point’s room is used to check wet-location requirements.',
        nl: 'De ruimte van een punt dient om de eisen voor vochtige ruimtes te controleren.',
      },
      fix: {
        fr: 'Dessinez la pièce avec l’outil Pièce.',
        en: 'Draw the room with the Room tool.',
        nl: 'Teken de ruimte met het gereedschap Ruimte.',
      },
      verified: true,
    },
    ({ project, planSheets, roomOf }) =>
      Object.values(project.elements)
        .filter((e) => planSheets.includes(e.sheetId) && pointKind(e) && e.tag && !roomOf.has(e.tag))
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: {
            fr: `${e.tag} : hors de toute pièce`,
            en: `${e.tag}: not inside a room`,
            nl: `${e.tag}: niet in een ruimte`,
          },
        })),
  ),
  rule(
    {
      id: 'R-CTL-01',
      severity: 'warning',
      title: { fr: 'Contact sans bobine', en: 'Contact without coil', nl: 'Contact zonder spoel' },
      why: {
        fr: 'Un contact appartient à un relais ou contacteur dont la bobine doit figurer au schéma (même repère).',
        en: 'A contact belongs to a relay or contactor whose coil must appear on the diagrams (same tag).',
        nl: 'Een contact hoort bij een relais of contactor waarvan de spoel op de schema’s moet staan (zelfde referentie).',
      },
      fix: {
        fr: 'Dessinez la bobine avec le même repère, ou corrigez le repère du contact.',
        en: 'Draw the coil with the same tag, or fix the contact’s tag.',
        nl: 'Teken de spoel met dezelfde referentie, of verbeter de referentie van het contact.',
      },
      verified: true,
    },
    ({ project }) =>
      [...controlInfo(project).relays.values()]
        .filter(
          (r) =>
            r.heads.length === 0 && r.contacts.some((c) => project.sheets[c.sheetId]?.kind !== 'single-line'),
        )
        .map((r) => ({
          targets: r.contacts.map((c) => c.id),
          sheetId: r.contacts[0]!.sheetId,
          message: {
            fr: `${r.tag} : contact(s) sans bobine`,
            en: `${r.tag}: contact(s) without a coil`,
            nl: `${r.tag}: contact(en) zonder spoel`,
          },
        })),
  ),
  rule(
    {
      id: 'R-CTL-02',
      severity: 'info',
      title: { fr: 'Bobine sans contact', en: 'Coil without contacts', nl: 'Spoel zonder contacten' },
      why: {
        fr: 'Une bobine dont aucun contact n’est dessiné ne commande rien sur les schémas.',
        en: 'A coil with no contact drawn switches nothing on the diagrams.',
        nl: 'Een spoel zonder getekend contact schakelt niets op de schema’s.',
      },
      fix: {
        fr: 'Ajoutez ses contacts (même repère) là où ils agissent.',
        en: 'Add its contacts (same tag) where they act.',
        nl: 'Voeg de contacten (zelfde referentie) toe waar ze werken.',
      },
      verified: true,
    },
    ({ project }) =>
      [...controlInfo(project).relays.values()]
        .filter(
          (r) =>
            r.heads.length > 0 && r.contacts.length === 0 && !r.heads[0]!.symbolId.startsWith('iec.overload'),
        )
        .map((r) => ({
          targets: r.heads.map((h) => h.id),
          sheetId: r.heads[0]!.sheetId,
          message: {
            fr: `${r.tag} : aucun contact dessiné`,
            en: `${r.tag}: no contact drawn`,
            nl: `${r.tag}: geen contact getekend`,
          },
        })),
  ),
  rule(
    {
      id: 'R-CTL-03',
      severity: 'error',
      title: { fr: 'Numéro de borne en double', en: 'Duplicate terminal number', nl: 'Dubbel klemnummer' },
      why: {
        fr: 'Dans un bornier, chaque borne a un numéro unique.',
        en: 'Within a terminal strip, each terminal has a unique number.',
        nl: 'Binnen een klemmenstrook heeft elke klem een uniek nummer.',
      },
      fix: {
        fr: 'Utilisez « Numéroter les bornes ».',
        en: 'Use “Number terminals”.',
        nl: 'Gebruik “Klemmen nummeren”.',
      },
      verified: true,
    },
    ({ project }) => {
      const seen = new Map<string, Element>();
      const out: ReturnType<Check> = [];
      for (const e of Object.values(project.elements)) {
        if (e.symbolId !== 'iec.terminal' || !e.tag || e.props.number === undefined || e.props.number === '')
          continue;
        const k = `${e.tag}:${e.props.number}`;
        const first = seen.get(k);
        if (first)
          out.push({
            targets: [e.id, first.id],
            sheetId: e.sheetId,
            message: {
              fr: `${k} : numéro en double`,
              en: `${k}: duplicate number`,
              nl: `${k}: dubbel nummer`,
            },
          });
        else seen.set(k, e);
      }
      return out;
    },
  ),
  rule(
    {
      id: 'R-DOC-04',
      severity: 'warning',
      title: {
        fr: 'Élément hors de la feuille',
        en: 'Element outside the sheet',
        nl: 'Element buiten het blad',
      },
      why: {
        fr: 'Ce qui dépasse du format n’apparaît pas à l’impression ni dans le PDF.',
        en: 'Anything beyond the paper is missing from prints and PDFs.',
        nl: 'Wat buiten het papier valt, ontbreekt op afdrukken en PDF’s.',
      },
      fix: {
        fr: 'Passez la feuille en A3, ou répartissez les circuits sur une feuille supplémentaire.',
        en: 'Switch the sheet to A3, or split the circuits onto another sheet.',
        nl: 'Zet het blad op A3, of verdeel de kringen over een ander blad.',
      },
      verified: true,
    },
    ({ project }) => {
      const out: ReturnType<Check> = [];
      const bySheet = new Map<Id, Element[]>();
      for (const e of Object.values(project.elements)) {
        const s = project.sheets[e.sheetId];
        const def = getSymbol(e.symbolId);
        if (!s || !def) continue;
        const { w, h } = paperSize(s);
        const b = elementBounds(def, e);
        if (b.minX < 0 || b.minY < 0 || b.maxX > w || b.maxY > h)
          bySheet.set(s.id, [...(bySheet.get(s.id) ?? []), e]);
      }
      for (const [sheetId, els] of bySheet)
        out.push({
          targets: els.map((e) => e.id),
          sheetId,
          message: {
            fr: `« ${project.sheets[sheetId]!.name} » : ${els.length} élément(s) hors du format`,
            en: `“${project.sheets[sheetId]!.name}”: ${els.length} element(s) beyond the paper`,
            nl: `“${project.sheets[sheetId]!.name}”: ${els.length} element(en) buiten het papier`,
          },
        });
      return out;
    },
  ),
  rule(
    {
      id: 'R-CALC-01',
      severity: 'warning',
      title: {
        fr: 'Courant d’emploi supérieur au calibre',
        en: 'Design current above the rating',
        nl: 'Bedrijfsstroom boven de nominale stroom',
      },
      why: {
        fr: 'Avec la puissance indiquée, le courant d’emploi Ib dépasse le calibre In : la protection risque de déclencher.',
        en: 'With the stated power, the design current Ib exceeds the rating In: the protection may trip.',
        nl: 'Met het opgegeven vermogen is de bedrijfsstroom Ib groter dan de nominale stroom In: de beveiliging kan uitschakelen.',
      },
      fix: {
        fr: 'Vérifiez la puissance (et le foisonnement), ou augmentez calibre et section.',
        en: 'Check the power (and diversity), or increase rating and section.',
        nl: 'Controleer het vermogen (en de gelijktijdigheid), of verhoog nominale stroom en doorsnede.',
      },
      verified: true,
    },
    ({ project, a }) => {
      const calcs = projectCalcs(project, a);
      return a.circuits.flatMap((c) => {
        const k = calcs.get(c.device.id);
        if (!k || k.ibSource !== 'loads' || k.ib === undefined || k.inA === undefined || k.ib <= k.inA)
          return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : Ib ${fmt(k.ib)} A > In ${k.inA} A`,
              en: `Circuit ${name(c)}: Ib ${fmt(k.ib, true)} A > In ${k.inA} A`,
              nl: `Kring ${name(c)}: Ib ${fmt(k.ib)} A > In ${k.inA} A`,
            },
          },
        ];
      });
    },
  ),
  rule(
    {
      id: 'R-CALC-02',
      severity: 'error',
      title: {
        fr: 'Câble insuffisant pour le calibre (Iz)',
        en: 'Cable too small for the rating (Iz)',
        nl: 'Kabel te klein voor de nominale stroom (Iz)',
      },
      why: {
        fr: 'Le courant admissible Iz du câble, selon son mode de pose, doit être au moins égal au calibre In (IEC 60364-5-52).',
        en: 'The cable’s current-carrying capacity Iz for its installation method must be at least the rating In (IEC 60364-5-52).',
        nl: 'De toelaatbare stroom Iz van de kabel voor zijn plaatsingswijze moet minstens gelijk zijn aan de nominale stroom In (IEC 60364-5-52).',
      },
      fix: {
        fr: 'Augmentez la section, changez le mode de pose ou réduisez le calibre.',
        en: 'Increase the section, change the installation method or lower the rating.',
        nl: 'Verhoog de doorsnede, wijzig de plaatsingswijze of verlaag de nominale stroom.',
      },
      verified: false,
    },
    ({ project, a }) => {
      const calcs = projectCalcs(project, a);
      return a.circuits.flatMap((c) => {
        const k = calcs.get(c.device.id);
        if (!k || k.iz === undefined || k.inA === undefined || k.inA <= k.iz) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : In ${k.inA} A > Iz ${fmt(k.iz)} A (pose ${k.method})`,
              en: `Circuit ${name(c)}: In ${k.inA} A > Iz ${fmt(k.iz, true)} A (method ${k.method})`,
              nl: `Kring ${name(c)}: In ${k.inA} A > Iz ${fmt(k.iz)} A (plaatsing ${k.method})`,
            },
          },
        ];
      });
    },
  ),
  rule(
    {
      id: 'R-CALC-03',
      severity: 'warning',
      title: { fr: 'Chute de tension élevée', en: 'High voltage drop', nl: 'Hoge spanningsval' },
      why: {
        fr: 'Chute de tension du circuit au-delà des valeurs usuelles : 3 % pour l’éclairage, 5 % pour les autres usages (IEC 60364-5-52, annexe G). Valeurs à confirmer pour le RGIE.',
        en: 'Circuit voltage drop beyond the usual values: 3 % for lighting, 5 % for other uses (IEC 60364-5-52, Annex G). To be confirmed for the RGIE.',
        nl: 'Spanningsval van de kring boven de gebruikelijke waarden: 3 % voor verlichting, 5 % voor andere toepassingen (IEC 60364-5-52, bijlage G). Te bevestigen voor het AREI.',
      },
      fix: {
        fr: 'Augmentez la section ou raccourcissez le circuit.',
        en: 'Increase the section or shorten the circuit.',
        nl: 'Verhoog de doorsnede of maak de kring korter.',
      },
      verified: false,
    },
    ({ project, a }) => {
      const calcs = projectCalcs(project, a);
      return a.circuits.flatMap((c) => {
        const k = calcs.get(c.device.id);
        if (!k || k.dropPct === undefined || k.dropPct <= k.dropLimitPct) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ΔU ${fmt(k.dropPct)} % (max. ${k.dropLimitPct} %)`,
              en: `Circuit ${name(c)}: ΔU ${fmt(k.dropPct, true)} % (max. ${k.dropLimitPct} %)`,
              nl: `Kring ${name(c)}: ΔU ${fmt(k.dropPct)} % (max. ${k.dropLimitPct} %)`,
            },
          },
        ];
      });
    },
  ),
  rule(
    {
      id: 'R-CALC-04',
      severity: 'error',
      title: {
        fr: 'Circuit trop long pour la protection',
        en: 'Circuit too long for its protection',
        nl: 'Kring te lang voor zijn beveiliging',
      },
      why: {
        fr: 'Le courant de court-circuit minimal en bout de câble doit faire déclencher le magnétique du disjoncteur (méthode conventionnelle).',
        en: 'The minimum short-circuit current at the end of the cable must trip the breaker’s magnetic release (conventional method).',
        nl: 'De kleinste kortsluitstroom aan het einde van de kabel moet de magnetische beveiliging van de automaat doen werken (conventionele methode).',
      },
      fix: {
        fr: 'Raccourcissez le circuit, augmentez la section ou choisissez une courbe plus basse (C → B).',
        en: 'Shorten the circuit, increase the section or pick a lower curve (C → B).',
        nl: 'Maak de kring korter, verhoog de doorsnede of kies een lagere curve (C → B).',
      },
      verified: false,
    },
    ({ project, a }) => {
      const calcs = projectCalcs(project, a);
      return a.circuits.flatMap((c) => {
        const k = calcs.get(c.device.id);
        if (!k || k.iccMinA === undefined || k.imA === undefined || k.iccMinA >= k.imA) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${k.lengthM} m > longueur max. ${Math.floor(k.lMaxM ?? 0)} m`,
              en: `Circuit ${name(c)}: ${k.lengthM} m > max. length ${Math.floor(k.lMaxM ?? 0)} m`,
              nl: `Kring ${name(c)}: ${k.lengthM} m > max. lengte ${Math.floor(k.lMaxM ?? 0)} m`,
            },
          },
        ];
      });
    },
  ),
  rule(
    {
      id: 'R-CALC-05',
      severity: 'error',
      title: {
        fr: 'Pouvoir de coupure insuffisant',
        en: 'Breaking capacity too low',
        nl: 'Onderbrekingsvermogen te laag',
      },
      why: {
        fr: 'Le pouvoir de coupure de chaque protection doit être au moins égal au courant de court-circuit présumé à l’origine.',
        en: 'Each protective device’s breaking capacity must be at least the prospective short-circuit current at the origin.',
        nl: 'Het onderbrekingsvermogen van elke beveiliging moet minstens gelijk zijn aan de verwachte kortsluitstroom aan de oorsprong.',
      },
      fix: {
        fr: 'Choisissez un appareil au pouvoir de coupure suffisant (ou vérifiez l’Icc communiqué par le GRD).',
        en: 'Choose a device with enough breaking capacity (or check the Icc given by the DSO).',
        nl: 'Kies een toestel met voldoende onderbrekingsvermogen (of controleer de kortsluitstroom van de netbeheerder).',
      },
      verified: true,
    },
    ({ project, a }) => {
      const icc = project.meta.iccOriginKa;
      if (!icc) return [];
      const calcs = projectCalcs(project, a);
      return a.circuits.flatMap((c) => {
        const k = calcs.get(c.device.id);
        if (!k || k.breakingKa === undefined || k.breakingKa >= icc) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${k.breakingKa} kA < Icc ${icc} kA`,
              en: `Circuit ${name(c)}: ${k.breakingKa} kA < Icc ${icc} kA`,
              nl: `Kring ${name(c)}: ${k.breakingKa} kA < Icc ${icc} kA`,
            },
          },
        ];
      });
    },
  ),
  rule(
    {
      id: 'R-CALC-00',
      severity: 'info',
      title: { fr: 'Longueurs manquantes', en: 'Missing lengths', nl: 'Ontbrekende lengtes' },
      why: {
        fr: 'Sans longueur de câble, la chute de tension et la longueur maximale protégée ne peuvent pas être calculées.',
        en: 'Without a cable length, the voltage drop and maximum protected length cannot be calculated.',
        nl: 'Zonder kabellengte kunnen de spanningsval en de maximale beveiligde lengte niet berekend worden.',
      },
      fix: {
        fr: 'Indiquez la longueur de chaque circuit dans le tableau des circuits.',
        en: 'Enter each circuit’s length in the circuit table.',
        nl: 'Vul de lengte van elke kring in de kringentabel in.',
      },
      verified: true,
    },
    ({ a }) => {
      const missing = a.circuits.filter((c) => c.circuit && !c.circuit.cable.lengthM && !c.circuit.spare);
      if (!missing.length) return [];
      return [
        {
          targets: missing.map((c) => c.device.id),
          sheetId: missing[0]!.sheet.id,
          message: {
            fr: `${missing.length} circuit(s) sans longueur : ${missing.map(name).join(', ')}`,
            en: `${missing.length} circuit(s) without a length: ${missing.map(name).join(', ')}`,
            nl: `${missing.length} kring(en) zonder lengte: ${missing.map(name).join(', ')}`,
          },
        },
      ];
    },
  ),
  rule(
    {
      id: 'R-ML-01',
      severity: 'warning',
      title: {
        fr: 'Feuille générée à mettre à jour',
        en: 'Generated sheet out of date',
        nl: 'Gemaakt blad niet meer actueel',
      },
      why: {
        fr: 'Le schéma unifilaire a changé depuis la génération du schéma multifilaire ou de l’implantation du tableau.',
        en: 'The single-line diagram changed since the multi-line diagram or board layout was generated.',
        nl: 'Het eendraadschema is gewijzigd sinds het meerdraadsschema of de bordindeling gemaakt werd.',
      },
      fix: {
        fr: 'Ouvrez la feuille et cliquez « Mettre à jour ».',
        en: 'Open the sheet and click “Update”.',
        nl: 'Open het blad en klik op “Bijwerken”.',
      },
      verified: true,
    },
    ({ project, a }) =>
      staleMultiLines(project, a).map((s) => ({
        targets: [],
        sheetId: s.id,
        message: {
          fr: `« ${s.name} » n’est plus à jour`,
          en: `“${s.name}” is out of date`,
          nl: `“${s.name}” is niet meer actueel`,
        },
      })),
  ),
  rule(
    {
      id: 'R-SRV-01',
      severity: 'warning',
      title: {
        fr: 'Schémas à régénérer depuis le relevé',
        en: 'Diagrams to regenerate from the survey',
        nl: 'Schema’s opnieuw te maken vanuit de opname',
      },
      why: {
        fr: 'Le relevé sur site a changé depuis que les schémas en ont été générés.',
        en: 'The site survey changed since the diagrams were generated from it.',
        nl: 'De opname ter plaatse is gewijzigd sinds de schema’s ervan gemaakt werden.',
      },
      fix: {
        fr: 'Menu Schéma › « Générer les schémas », ou l’onglet Contrôle du relevé sur site (les feuilles générées sont remplacées).',
        en: 'Diagram menu › “Generate diagrams”, or the Checks tab of the site survey (generated sheets are replaced).',
        nl: 'Menu Schema › “Schema’s maken”, of het tabblad Controle van de opname ter plaatse (de gemaakte bladen worden vervangen).',
      },
      verified: true,
    },
    ({ project }) => {
      const sheets = Object.values(project.sheets).filter((s) => s.source?.kind === 'survey');
      if (!sheets.length) return [];
      const sig = surveySignature(project);
      return sheets
        .filter((s) => s.source!.signature !== sig && (!s.source!.part || s.source!.part === 1))
        .map((s) => ({
          targets: [],
          sheetId: s.id,
          message: {
            fr: `« ${s.name} » ne suit plus le relevé`,
            en: `“${s.name}” no longer matches the survey`,
            nl: `“${s.name}” volgt de opname niet meer`,
          },
        }));
    },
  ),
]);
